import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18n from '@/i18n';
import {
  courseAppearanceService,
  type UpdatePageSectionInput,
  type PageSection,
} from '@/services/course-appearance.service';
import { appearanceKeys } from './queryKeys';

interface Vars {
  sectionId: string;
  input: UpdatePageSectionInput;
}

/** Update a section (config / visibility / type / order) optimistically. */
export function useUpdatePageSection(productId: string) {
  const qc = useQueryClient();
  const key = appearanceKeys.sections(productId);

  return useMutation({
    mutationFn: ({ sectionId, input }: Vars) =>
      courseAppearanceService.updatePageSection(productId, sectionId, input),
    onMutate: async ({ sectionId, input }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<PageSection[]>(key);
      if (prev) {
        qc.setQueryData<PageSection[]>(
          key,
          prev.map((s) =>
            s.id === sectionId ? ({ ...s, ...input } as PageSection) : s,
          ),
        );
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error(i18n.t('adminAppearance:toasts.updateSectionError'));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
